/**
 * LoginPage Component
 *
 * The main login page for Auth.HireItNow.com.
 * Provides multiple sign-in options:
 * - Email/Password (primary, shown by default)
 * - Microsoft (Entra ID) for enterprise users (secondary)
 * - Sign up for new users
 * - Forgot password flow
 */

import { useState, useCallback, useMemo, useEffect, type FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../auth';
import { sendPasswordResetEmail } from '../auth/firebase';
import { Loading } from './Loading';

// Import construction background images
import heroBg1 from '../../../common/assets/images/hero-construction-1.jpg';
import heroBg4 from '../../../common/assets/images/hero-construction-4.jpg';

/**
 * Get background image URL based on current time
 * 9:00-16:00 (business hours): hero-construction-1.jpg
 * Outside business hours: hero-construction-4.jpg
 */
function getBackgroundImage(): string {
  const currentHour = new Date().getHours();
  const isDayTime = currentHour >= 9 && currentHour < 16;
  const imageUrl = isDayTime ? heroBg1 : heroBg4;
  return `url('${imageUrl}')`;
}

type PortalTheme = 'partner' | 'hirer' | 'supplier' | 'admin' | 'hirenow';

const portalIconConfig: Record<PortalTheme, { logoColor: string; favicon: string }> = {
  partner: { logoColor: '#2563eb', favicon: '/favicon-blue.svg' },
  hirer: { logoColor: '#16a34a', favicon: '/favicon-green.svg' },
  supplier: { logoColor: '#f97316', favicon: '/favicon-orange.svg' },
  admin: { logoColor: '#111827', favicon: '/favicon-black.svg' },
  hirenow: { logoColor: '#1e4d3e', favicon: '/favicon-hirenow.svg' },
};

function parsePortalFromValue(value: string | null): PortalTheme | null {
  if (!value) {
    return null;
  }

  const normalized = value.toLowerCase();

  if (normalized.includes('partner')) return 'partner';
  if (normalized.includes('hirer')) return 'hirer';
  if (normalized.includes('supplier')) return 'supplier';
  if (normalized.includes('admin')) return 'admin';

  return null;
}

function getPortalThemeFromContext(): PortalTheme {
  const params = new URLSearchParams(window.location.search);
  const portalFromQuery = parsePortalFromValue(params.get('portal'));
  if (portalFromQuery) {
    return portalFromQuery;
  }

  const returnTo = params.get('returnTo') || sessionStorage.getItem('auth_returnTo');
  const portalFromReturnTo = parsePortalFromValue(returnTo);
  if (portalFromReturnTo) {
    return portalFromReturnTo;
  }

  const portalFromPath = parsePortalFromValue(window.location.pathname);
  if (portalFromPath) {
    return portalFromPath;
  }

  return 'hirenow';
}

function setFaviconForTheme(theme: PortalTheme): void {
  const iconHref = `${portalIconConfig[theme].favicon}?theme=${theme}`;
  const rels = ['icon', 'shortcut icon'];

  rels.forEach((rel) => {
    let link = document.querySelector(`link[rel='${rel}']`) as HTMLLinkElement | null;

    if (!link) {
      link = document.createElement('link');
      link.rel = rel;
      document.head.appendChild(link);
    }

    link.type = 'image/svg+xml';
    link.href = iconHref;
  });
}

/**
 * LoginPage Component
 *
 * Displays the Hire It Now branding and sign-in options.
 * Email/password form is shown by default with Microsoft as an alternative.
 */
export function LoginPage() {
  const { login, register, switchProvider, isLoading, error, clearError } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const backgroundImage = useMemo(() => getBackgroundImage(), []);
  const portalTheme = useMemo(() => getPortalThemeFromContext(), []);
  const logoColor = portalIconConfig[portalTheme].logoColor;
  const [isSigningIn, setIsSigningIn] = useState(false);
  const initialMode = searchParams.get('mode') === 'signup' ? 'signup' : 'signin';
  const [mode, setMode] = useState<'signin' | 'signup' | 'forgot'>(initialMode);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  useEffect(() => {
    setFaviconForTheme(portalTheme);
  }, [portalTheme]);

  /**
   * Handle Microsoft sign-in button click
   */
  const handleMicrosoftSignIn = useCallback(async () => {
    setIsSigningIn(true);
    clearError();
    setLocalError(null);
    setSuccessMessage(null);

    try {
      switchProvider('firebase-microsoft');
      await login({ interaction: 'popup' });
    } catch (err) {
      console.error('[LoginPage] Microsoft sign-in failed:', err);
    } finally {
      setIsSigningIn(false);
    }
  }, [login, switchProvider, clearError]);

  /**
   * Handle Google sign-in button click
   */
  const handleGoogleSignIn = useCallback(async () => {
    setIsSigningIn(true);
    clearError();
    setLocalError(null);
    setSuccessMessage(null);

    try {
      switchProvider('firebase-google');
      await login({ interaction: 'popup' });
    } catch (err) {
      console.error('[LoginPage] Google sign-in failed:', err);
    } finally {
      setIsSigningIn(false);
    }
  }, [login, switchProvider, clearError]);

  /**
   * Handle email/password sign-in form submission
   */
  const handleEmailSignIn = useCallback(async (e: FormEvent) => {
    e.preventDefault();
    
    if (!email || !password) {
      return;
    }

    setIsSigningIn(true);
    clearError();
    setLocalError(null);
    setSuccessMessage(null);

    try {
      switchProvider('firebase-email');
      await login({ email, password });
    } catch (err) {
      console.error('[LoginPage] Email sign-in failed:', err);
    } finally {
      setIsSigningIn(false);
    }
  }, [login, switchProvider, clearError, email, password]);

  /**
   * Handle sign-up form submission
   */
  const handleSignUp = useCallback(async (e: FormEvent) => {
    e.preventDefault();
    
    if (!email || !password) {
      return;
    }

    // Validate password strength
    if (password.length < 6) {
      setLocalError('Password must be at least 6 characters');
      return;
    }

    setIsSigningIn(true);
    clearError();
    setLocalError(null);
    setSuccessMessage(null);

    try {
      await register(email, password);
    } catch (err) {
      console.error('[LoginPage] Sign-up failed:', err);
    } finally {
      setIsSigningIn(false);
    }
  }, [register, clearError, email, password]);

  /**
   * Handle forgot password form submission
   */
  const handleForgotPassword = useCallback(async (e: FormEvent) => {
    e.preventDefault();
    
    if (!email) {
      return;
    }

    setIsSigningIn(true);
    clearError();
    setLocalError(null);
    setSuccessMessage(null);

    try {
      await sendPasswordResetEmail(email);
      setSuccessMessage('Password reset email sent. Check your inbox.');
    } catch (err) {
      console.error('[LoginPage] Password reset failed:', err);
      const firebaseError = err as { code?: string };
      if (firebaseError.code === 'auth/user-not-found') {
        setLocalError('No account found with this email address');
      } else if (firebaseError.code === 'auth/invalid-email') {
        setLocalError('Invalid email address');
      } else {
        setLocalError('Failed to send password reset email. Please try again.');
      }
    } finally {
      setIsSigningIn(false);
    }
  }, [clearError, email]);

  /**
   * Switch between modes
   */
  const switchMode = useCallback((newMode: 'signin' | 'signup' | 'forgot') => {
    setMode(newMode);
    clearError();
    setLocalError(null);
    setSuccessMessage(null);
    setPassword('');
  }, [clearError]);

  // Show loading state during initial auth check
  if (isLoading && !isSigningIn) {
    return <Loading fullScreen message="Checking authentication..." />;
  }

  const isSignUp = mode === 'signup';
  const isForgot = mode === 'forgot';

  // Get title and subtitle based on mode
  const getTitle = () => {
    if (isForgot) return 'Reset Password';
    if (isSignUp) return 'Create Account';
    return 'Welcome';
  };

  const getSubtitle = () => {
    if (isForgot) return 'Enter your email to receive a password reset link';
    if (isSignUp) return 'Sign up to access your Hire It Now account';
    return 'Sign in to access your Hire It Now account';
  };

  // Get form submit handler based on mode
  const getFormHandler = () => {
    if (isForgot) return handleForgotPassword;
    if (isSignUp) return handleSignUp;
    return handleEmailSignIn;
  };

  return (
    <div className="login-page" style={{ backgroundImage }}>
      <div className="login-container">
        {/* Logo and Branding */}
        <div className="login-header">
          <div className="logo">
            <svg
              viewBox="0 0 40 40"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="logo-icon"
              aria-hidden="true"
            >
              <rect width="40" height="40" rx="8" fill={logoColor} />
              <path
                d="M12 14h6v12h-6zM22 14h6v12h-6zM12 20h16"
                stroke="white"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
            <span className="logo-text">
              Hire <span className="logo-text-accent" style={{ color: logoColor }}>It</span> Now
            </span>
          </div>
          <h1 className="login-title">{getTitle()}</h1>
          <p className="login-subtitle">{getSubtitle()}</p>
        </div>

        {/* Success Message */}
        {successMessage && (
          <div className="login-success" role="status">
            <span className="success-icon" aria-hidden="true">✓</span>
            <p className="success-message">{successMessage}</p>
          </div>
        )}

        {/* Error Display */}
        {(error || localError) && (
          <div className="login-error" role="alert">
            <span className="error-icon" aria-hidden="true">
              !
            </span>
            <div className="error-content">
              <p className="error-message">{error?.message || localError}</p>
              <button
                type="button"
                className="error-dismiss"
                onClick={() => {
                  clearError();
                  setLocalError(null);
                }}
                aria-label="Dismiss error"
              >
                Dismiss
              </button>
            </div>
          </div>
        )}

        {/* Email/Password Form */}
        <form 
          className="email-login-form" 
          onSubmit={getFormHandler()}
        >
          <div className="form-group">
            <label htmlFor="email" className="form-label">
              Email address
            </label>
            <input
              type="email"
              id="email"
              className="form-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              required
              disabled={isSigningIn}
              autoComplete="email"
            />
          </div>

          {/* Password field - not shown for forgot password */}
          {!isForgot && (
            <div className="form-group">
              <label htmlFor="password" className="form-label">
                Password
              </label>
              <input
                type="password"
                id="password"
                className="form-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={isSignUp ? 'Create a password' : 'Enter your password'}
                required
                disabled={isSigningIn}
                autoComplete={isSignUp ? 'new-password' : 'current-password'}
                minLength={isSignUp ? 6 : undefined}
              />
            </div>
          )}

          {/* Forgot password link - only for sign in */}
          {!isSignUp && !isForgot && (
            <div className="form-link-row">
              <button
                type="button"
                className="link-button"
                onClick={() => switchMode('forgot')}
                disabled={isSigningIn}
              >
                Forgot password?
              </button>
            </div>
          )}

          <button
            type="submit"
            className="btn btn-primary btn-signin"
            disabled={isSigningIn || !email || (!isForgot && !password)}
            aria-busy={isSigningIn}
          >
            {isSigningIn ? (
              <>
                <span className="btn-spinner" aria-hidden="true" />
                {isForgot ? 'Sending...' : isSignUp ? 'Creating account...' : 'Signing in...'}
              </>
            ) : (
              isForgot ? 'Send reset link' : isSignUp ? 'Create account' : 'Sign in'
            )}
          </button>
        </form>

        {/* Divider - not shown for forgot password */}
        {!isForgot && (
          <div className="login-divider">
            <span>or</span>
          </div>
        )}

        {/* Microsoft Sign-in - not shown for forgot password */}
        {!isForgot && (
          <div className="login-social-buttons">
            <button
              type="button"
              className="btn btn-secondary btn-signin btn-microsoft"
              onClick={handleMicrosoftSignIn}
              disabled={isSigningIn}
              aria-busy={isSigningIn}
            >
              {isSigningIn ? (
                <>
                  <span className="btn-spinner btn-spinner-secondary" aria-hidden="true" />
                  {isSignUp ? 'Signing up...' : 'Signing in...'}
                </>
              ) : (
                <>
                  <MicrosoftIcon />
                  {isSignUp ? 'Sign up with Microsoft' : 'Sign in with Microsoft'}
                </>
              )}
            </button>

            <button
              type="button"
              className="btn btn-secondary btn-signin btn-google"
              onClick={handleGoogleSignIn}
              disabled={isSigningIn}
              aria-busy={isSigningIn}
            >
              {isSigningIn ? (
                <>
                  <span className="btn-spinner btn-spinner-secondary" aria-hidden="true" />
                  {isSignUp ? 'Signing up...' : 'Signing in...'}
                </>
              ) : (
                <>
                  <GoogleIcon />
                  {isSignUp ? 'Sign up with Google' : 'Sign in with Google'}
                </>
              )}
            </button>
          </div>
        )}

        {/* Toggle Sign In / Sign Up / Back to sign in */}
        <div className="login-toggle">
          {isForgot ? (
            <p>
              Remember your password?{' '}
              <button
                type="button"
                className="link-button"
                onClick={() => switchMode('signin')}
                disabled={isSigningIn}
              >
                Sign in
              </button>
            </p>
          ) : (
            <p>
              {isSignUp ? 'Already have an account?' : "Don't have an account?"}{' '}
              <button
                type="button"
                className="link-button"
                onClick={() => isSignUp ? switchMode('signin') : navigate('/signup')}
                disabled={isSigningIn}
              >
                {isSignUp ? 'Sign in' : 'Sign up'}
              </button>
            </p>
          )}
        </div>

        {/* Footer */}
        <div className="login-footer">
          <p className="login-help">
            Need help?{' '}
            <a href="mailto:support@hirenow.com" className="help-link">
              Contact Support
            </a>
          </p>
          <p className="login-terms">
            By signing in, you agree to our{' '}
            <a href="/terms" className="terms-link">
              Terms of Service
            </a>{' '}
            and{' '}
            <a href="/privacy" className="privacy-link">
              Privacy Policy
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}

/**
 * Microsoft Icon Component
 * SVG icon for the Microsoft sign-in button
 */
function MicrosoftIcon() {
  return (
    <svg
      className="btn-icon"
      viewBox="0 0 21 21"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <rect x="1" y="1" width="9" height="9" fill="#f25022" />
      <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
      <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
      <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
    </svg>
  );
}

function GoogleIcon() {
  return (
    <svg
      className="btn-icon"
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        fill="#EA4335"
        d="M12 10.2v3.9h5.5c-.2 1.3-1.5 3.8-5.5 3.8-3.3 0-6-2.7-6-6s2.7-6 6-6c1.9 0 3.2.8 3.9 1.4l2.7-2.6C16.9 3.1 14.7 2 12 2 6.9 2 2.8 6.1 2.8 11.2S6.9 20.4 12 20.4c6.9 0 9.2-4.8 9.2-7.3 0-.5 0-.9-.1-1.3H12z"
      />
    </svg>
  );
}
